import apiClient from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/config';
import type {
  BillingPlansResponse,
  ProviderInfo,
  Subscription,
  SubscribeRequest,
  SubscribeResponse,
  SeatsRequest,
  CoresRequest,
  SSOAddonRequest,
  InvoicesResponse,
  Invoice,
  PaymentMethodsResponse,
  TopUpRequest,
  TopUpResponse,
} from '../types';

export const billingApi = {
  async getPlans(): Promise<BillingPlansResponse> {
    const { data } = await apiClient.get<BillingPlansResponse>(API_ENDPOINTS.BILLING.PLANS);
    return data;
  },

  async getProvider(): Promise<ProviderInfo> {
    const { data } = await apiClient.get<ProviderInfo>(API_ENDPOINTS.BILLING.PROVIDER);
    return data;
  },

  async getSubscription(): Promise<Subscription | null> {
    // 404 "no_subscription" is a normal pre-subscribe state; treat it as a
    // successful resolution so the global axios interceptor doesn't emit
    // a `Client error:` warning on every BillingPage mount.
    const resp = await apiClient.get<Subscription>(API_ENDPOINTS.BILLING.SUBSCRIPTION, {
      validateStatus: (s) => s === 404 || (s >= 200 && s < 300),
    });
    if (resp.status === 404) return null;
    return resp.data;
  },

  async subscribe(req: SubscribeRequest): Promise<SubscribeResponse> {
    const { data } = await apiClient.post<SubscribeResponse>(API_ENDPOINTS.BILLING.SUBSCRIBE, req);
    return data;
  },

  async cancelSubscription(): Promise<Subscription> {
    const { data } = await apiClient.delete<Subscription>(API_ENDPOINTS.BILLING.SUBSCRIPTION);
    return data;
  },

  async updateSeats(req: SeatsRequest): Promise<Subscription> {
    const { data } = await apiClient.patch<Subscription>(API_ENDPOINTS.BILLING.SEATS, req);
    return data;
  },

  async updateCores(req: CoresRequest): Promise<{ status: string }> {
    const { data } = await apiClient.patch<{ status: string }>(API_ENDPOINTS.BILLING.CORES, req);
    return data;
  },

  async toggleSSOAddon(req: SSOAddonRequest): Promise<{ status: string; enabled: boolean }> {
    const { data } = await apiClient.post<{ status: string; enabled: boolean }>(
      API_ENDPOINTS.BILLING.SSO_ADDON,
      req,
    );
    return data;
  },

  async listInvoices(before?: string): Promise<InvoicesResponse> {
    const { data } = await apiClient.get<InvoicesResponse>(API_ENDPOINTS.BILLING.INVOICES, {
      params: before ? { before } : {},
    });
    return data;
  },

  async getInvoice(id: string): Promise<Invoice> {
    const { data } = await apiClient.get<Invoice>(API_ENDPOINTS.BILLING.INVOICE_BY_ID(id));
    return data;
  },

  async listPaymentMethods(): Promise<PaymentMethodsResponse> {
    const { data } = await apiClient.get<PaymentMethodsResponse>(API_ENDPOINTS.BILLING.PAYMENT_METHODS);
    return data;
  },

  async detachPaymentMethod(id: string): Promise<{ status: string }> {
    const { data } = await apiClient.delete<{ status: string }>(
      API_ENDPOINTS.BILLING.PAYMENT_METHOD_BY_ID(id),
    );
    return data;
  },

  async topUp(req: TopUpRequest): Promise<TopUpResponse> {
    const { data } = await apiClient.post<TopUpResponse>(API_ENDPOINTS.BILLING.TOPUP, req);
    return data;
  },
};

export default billingApi;
